import {
  Editor,
  Extension,
  ParentConfig,
  Range,
  callOrReturn,
  getExtensionField,
} from '@tiptap/core';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';

import {
  type CommandItemProps,
  commandMenuState,
} from '../../private/components/CommandsMenu';

declare module '@tiptap/core' {
  interface NodeConfig<Options, Storage> {
    /**
     * Add a command option to the node
     */
    addCommandOption?:
      | null
      | ((this: {
          name: string;
          options: Options;
          storage: Storage;
          parent: ParentConfig<NodeConfig<Options>>['addCommandOption'];
        }) => CommandItemProps[]);
  }
}

export type CommandExtensionOptions<T> = {
  commands: CommandItemProps[];
  suggestion: Omit<SuggestionOptions<T>, 'editor'>;
};

export const commandExtension = Extension.create<CommandExtensionOptions<any>>({
  name: 'slash-command',
  addOptions() {
    return {
      commands: [],
      suggestion: {
        char: '/',
        command: ({
          editor,
          range,
          props,
        }: {
          editor: Editor;
          range: Range;
          props: CommandItemProps;
        }) => {
          if (props.nextMenu) {
            commandMenuState
              .getState()
              .changeMenu(props.nextMenu.type, props.nextMenu);
            return;
          }
          props.command?.({ editor, range });
        },
      },
    };
  },
  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
  extendNodeSchema(extension) {
    return {
      addCommandOption: callOrReturn(
        getExtensionField(extension, 'addCommandOption', extension),
        this,
      ),
    };
  },
  // @ts-expect-error
  addCommands() {
    return {
      getSlashCommandsItems: () => () => {
        const items = this.editor.extensionManager.extensions.flatMap(
          (item) => {
            return item.config.addCommandOption?.(this) ?? [];
          },
        );
        return [...this.options.commands, ...items];
      },
    };
  },
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    slashCommand: {
      getSlashCommandsItems(): CommandItemProps[];
    };
  }
}
